import { ProcessGeneral } from 'api/endpoints/processes';
import { TmsConnectionDetails } from 'api/endpoints/tmsConnections';
import BaseRepo from 'container/BaseRepo';
import {
  buildBadRequestApiError,
  buildNotFoundApiError,
} from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequest } from 'container/search';
import {
  CloseTMTPORequest,
  CreateTMTPOInvoiceRequest,
  FinishTaskValues,
  MaintenancePayablesScenario,
  MaintenancePayablesTask,
  MaintenancePayablesTaskBase,
  SearchMaintenancePayablesTasksRequest,
  TMTPOSection,
} from 'features/maintenancePayables/types';
import {
  CreateExtractionPlanRequest,
  ExtractionPlan,
  SearchExtractionPlanRequest,
  TmsDataModel,
  UpdateExtractionPlanType,
} from 'features/shared/licensedModules/types';

import { IMaintenancePayablesRepo } from './IMaintenancePayablesRepo';
import {
  CreateMaintenancePayablesScenarioRequest,
  MaintenancePayablesSearchResult,
  SearchMaintenancePayablesScenariosRequest,
  TMTVersion,
  UpdateScenarioRequestType,
} from './typing';

export class MaintenancePayablesRepo
  extends BaseRepo
  implements IMaintenancePayablesRepo
{
  getVersion = (
    scenario: MaintenancePayablesScenario
  ): ApiResponseAsync<TMTVersion> => {
    const link = scenario._links ? scenario._links['getTmtVersion'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get TMT version" is not available')
        )
      );

    return this.helpers.handlePost(link.href, {});
  };
  getTaskByIdExtended = (
    task: MaintenancePayablesTaskBase
  ): ApiResponseAsync<MaintenancePayablesTask> => {
    const link = task._links ? task._links['self'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "self" is not available'))
      );

    return this.helpers.handleGet<MaintenancePayablesTask>(link.href, {
      responseStopTransformPaths: ['data.field_values', 'data.classify_result'],
    });
  };
  finishTask = async (
    values: FinishTaskValues,
    task: MaintenancePayablesTask
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['finish'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "finish" is not available'))
      );
    return this.helpers.handlePostAction(link.href, values);
  };
  createTMOTPOInvoice = (
    task: MaintenancePayablesTask,
    request: CreateTMTPOInvoiceRequest
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['createTmtpoInvoice'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "create TMT PO invoice" is not available')
        )
      );
    }

    return this.helpers.handlePostAction<CreateTMTPOInvoiceRequest>(
      link.href,
      request
    );
  };
  closeTMTPO = (
    task: MaintenancePayablesTask,
    request: CloseTMTPORequest
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['closeTmtpo'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "close TMT PO" is not available')
        )
      );
    }

    return this.helpers.handlePostAction<CloseTMTPORequest>(link.href, request);
  };
  getTMTPOSection = (
    task: MaintenancePayablesTask,
    poNumber: string
  ): ApiResponseAsync<TMTPOSection> => {
    const link = task._links ? task._links['getTmtpoSection'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get TMT PO section" is not available')
        )
      );
    }

    const finalUrl =
      link.href +
      '?' +
      new URLSearchParams({
        po_number: poNumber,
      }).toString();

    return this.helpers.handleGet<TMTPOSection>(finalUrl);
  };

  lockTask = (
    task: MaintenancePayablesTaskBase
  ): ApiResponseAsync<MaintenancePayablesTaskBase | undefined> => {
    const link = task._links ? task._links['lock'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "lock" is not available'))
      );
    }

    return this.helpers.handlePost(link.href, task);
  };
  skipTask = (
    task: MaintenancePayablesTask
  ): ApiResponseAsync<MaintenancePayablesTaskBase | undefined> => {
    const link = task._links ? task._links['skip'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "skip" is not available'))
      );
    }

    return this.helpers.handlePost(link.href, task);
  };

  searchScenarios = async (
    draft: ProcessGeneral,
    request: SearchMaintenancePayablesScenariosRequest
  ): ApiResponseAsync<MaintenancePayablesScenario[]> => {
    const link = draft._links
      ? draft._links['searchMaintenancePayablesScenarios']
      : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "search maintenance payables scenarios" is not available'
          )
        )
      );
    }

    return this.helpers
      .executeSearch<MaintenancePayablesSearchResult>(link, request)
      .then((searchResult) => {
        if (isApiError(searchResult)) {
          return searchResult;
        }
        if (!searchResult) searchResult = [];
        return searchResult;
      });
  };

  lockNextDocument = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<MaintenancePayablesTaskBase | undefined> => {
    const link = draft._links
      ? draft._links['lockNextMaintenancePayablesDocument']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "lock next maintenance payables document" is not available'
          )
        )
      );

    return this.helpers.handlePost<
      undefined,
      MaintenancePayablesTask | undefined
    >(link.href, undefined);
  };

  getTaskById = async (
    draft: ProcessGeneral,
    taskId: string
  ): ApiResponseAsync<MaintenancePayablesTaskBase> => {
    const link = draft._links
      ? draft._links['searchMaintenancePayablesTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search maintenance payables tasks" is not available')
      );

    const request: SearchRequest = {
      top: 1,
      filter: {
        operator: 'AND',
        items: [
          {
            field: 'id',
            operation: 'equal',
            value: taskId,
            not: false,
          },
        ],
      },
    };

    const result = await this.helpers.executeSearch<
      MaintenancePayablesTaskBase[]
    >(link, request);

    if (isApiError(result)) return result;

    if (!result || result.length === 0)
      return buildNotFoundApiError(
        new Error('no maintenance payables task by id was found')
      );

    return result[0];
  };

  searchMaintenancePayablesExtractionPlans = async (
    draft: ProcessGeneral,
    request: SearchExtractionPlanRequest
  ): ApiResponseAsync<ExtractionPlan[]> => {
    const link = draft._links
      ? draft._links['searchMaintenancePayablesExtractionPlans']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error(
          'link "search maintenance payables extraction forms" is not available'
        )
      );

    const result = await this.helpers.executeSearch<ExtractionPlan[]>(
      link,
      request,
      { convertRequestToSnakeCase: true }
    );

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    return result;
  };

  createScenario = async (
    draft: ProcessGeneral,
    request: CreateMaintenancePayablesScenarioRequest
  ): ApiResponseAsync<MaintenancePayablesScenario> => {
    const link = draft._links
      ? draft._links['createMaintenancePayablesScenario']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error(
          'link "create maintenance payables scenario request" is not available'
        )
      );

    return this.helpers.handlePost<
      CreateMaintenancePayablesScenarioRequest,
      MaintenancePayablesScenario
    >(link.href, request);
  };

  updateScenario = async (
    scenario: MaintenancePayablesScenario,
    request: UpdateScenarioRequestType
  ): ApiResponseAsync<MaintenancePayablesScenario> => {
    const link = scenario._links ? scenario._links['update'] : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "update" is not available')
      );

    return this.helpers.executePatch(link, request);
  };

  setActivateScenario = async (
    scenario: MaintenancePayablesScenario,
    activate: boolean
  ): ApiResponseAsync<MaintenancePayablesScenario> => {
    let link = undefined;
    if (!scenario.isActive && activate && scenario._links?.['activate']) {
      link = scenario._links['activate'];
    } else if (
      scenario.isActive &&
      !activate &&
      scenario._links?.['deactivate']
    ) {
      link = scenario._links['deactivate'];
    }

    if (!link)
      return buildBadRequestApiError(
        new Error(
          `link "${activate ? 'activate' : 'deactivate'}" is not available`
        )
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  deleteScenario = async (
    scenario: MaintenancePayablesScenario
  ): ApiResponseAsync<void> => {
    const link = scenario._links ? scenario._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "delete scenario" is not available')
        )
      );

    return this.helpers.handleDelete(link.href);
  };

  createExtractionPlan = async (
    draft: ProcessGeneral,
    request: CreateExtractionPlanRequest
  ): ApiResponseAsync<ExtractionPlan> => {
    const link = draft._links
      ? draft._links['createMaintenancePayablesExtractionPlan']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "create maintenance payables extraction plan" is not available'
          )
        )
      );

    return this.helpers.handlePost<CreateExtractionPlanRequest, ExtractionPlan>(
      link.href,
      request
    );
  };

  deleteExtractionPlan = async (
    extractionPlan: ExtractionPlan
  ): ApiResponseAsync<void> => {
    const link = extractionPlan._links
      ? extractionPlan._links['delete']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  updateExtractionPlan = async (
    extractionPlan: ExtractionPlan,
    request: UpdateExtractionPlanType
  ): ApiResponseAsync<ExtractionPlan> => {
    const link = extractionPlan._links
      ? extractionPlan._links['update']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "update" is not available'))
      );

    return this.helpers.executePatch(link, request);
  };

  searchMaintenancePayablesTasks = async (
    draft: ProcessGeneral,
    request: SearchMaintenancePayablesTasksRequest
  ): ApiResponseAsync<MaintenancePayablesTaskBase[]> => {
    const link = draft._links
      ? draft._links['searchMaintenancePayablesTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search maintenance payables tasks" is not available')
      );

    const result = await this.helpers.executeSearch<
      MaintenancePayablesTaskBase[]
    >(link, request);

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    return result;
  };

  deleteMaintenancePayablesTask = async (
    task: MaintenancePayablesTask
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  unlockTask = async (
    task: MaintenancePayablesTaskBase
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['cancel'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "cancel" is not available'))
      );

    return this.helpers.handlePostAction(link.href, task);
  };

  getMaintenancePayablesModel = async (
    tms: TmsConnectionDetails
  ): ApiResponseAsync<TmsDataModel> => {
    const link = tms._links
      ? tms._links['getMaintenancePayablesModel']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get maintenance payables model" is not available')
        )
      );

    const url = new URL(link.href);
    url.searchParams.append('tms_version', '1');
    return this.helpers.handleGet<TmsDataModel>(url.toString());
  };
}
